import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ProjectService from "../../services/project.service";


export const getProject = createAsyncThunk(
    "project/getProject",
    async (params, thunkAPI) => {
        try {
            const response = await ProjectService.getProject(params);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                error.response?.data?.message || error.message
            );
        }
    }
);

export const getSite = createAsyncThunk(
    "project/getSite",
    async (params, thunkAPI) => {
        try {
            const response = await ProjectService.getSite(params);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                error.response?.data?.message || error.message
            );
        }
    }
);

export const addProject = createAsyncThunk(
    "project/addProject",
    async (data, thunkAPI) => {
        try {
            const response = await ProjectService.addProject(data);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                error.response?.data?.message || error.message
            );
        }
    }
);


export const addSite = createAsyncThunk(
    "project/addSite",
    async (data, thunkAPI) => {
        try {
            const response = await ProjectService.addSite(data);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                error.response?.data?.message || error.message
            );
        }
    }
);


export const updateProject = createAsyncThunk(
    "project/updateProject",
    async (payload, thunkAPI) => {
        try {
            const response = await ProjectService.editProject(payload);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                error.response?.data?.message || error.message
            );
        }
    }
);

export const updateSite = createAsyncThunk(
    "project/updateSite",
    async (payload, thunkAPI) => {
        try {
            const response = await ProjectService.editSite(payload);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                error.response?.data?.message || error.message
            );
        }
    }
);

export const deleteProject = createAsyncThunk(
    "project/deleteProject",
    async (id, thunkAPI) => {
        try {
            await ProjectService.deleteProject(id);
            return id;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                error.response?.data?.message || error.message
            );
        }
    }
);

export const deleteSite = createAsyncThunk(
    "project/deleteSite",
    async (id, thunkAPI) => {
        try {
            await ProjectService.deleteSite(id);
            return id;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                error.response?.data?.message || error.message
            );
        }
    }
);



const initialState = {
    loading: false,
    error: null,
    projectList: [],
    siteList: [],
    updateSuccess: false,
};



const projectSlice = createSlice({
    name: "project",
    initialState,
    reducers: {
        resetUpdateStatus: (state) => {
            state.updateSuccess = false;
        },
    },
    extraReducers: (builder) => {
        builder

            // ================= GET SITE =================

            .addCase(getSite.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getSite.fulfilled, (state, action) => {
                state.loading = false;

                if (Array.isArray(action.payload?.data)) {
                    state.siteList = action.payload.data;
                }
            })
            .addCase(getSite.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })


            // ================= ADD SITE =================

            .addCase(addSite.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(addSite.fulfilled, (state, action) => {
                state.loading = false;

                // Handles both single site and bulk upload
                if (Array.isArray(action.payload?.data)) {
                    state.siteList.unshift(...action.payload.data);
                } else {
                    const newSite =
                        action.payload?.data ||
                        action.payload?.site ||
                        action.payload;

                    if (newSite) {
                        state.siteList.unshift(newSite);
                    }
                }
            })
            .addCase(addSite.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // ================= UPDATE SITE =================

            .addCase(updateSite.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.updateSuccess = false;
            })
            .addCase(updateSite.fulfilled, (state, action) => {
                state.loading = false;
                state.updateSuccess = true;

                const updated =
                    action.payload?.data ||
                    action.payload?.site;

                if (updated?._id && Array.isArray(state.siteList)) {
                    const index = state.siteList.findIndex(
                        (item) => item._id === updated._id
                    );

                    if (index !== -1) {
                        state.siteList[index] = updated;
                    }
                }
            })
            .addCase(updateSite.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // ================= DELETE SITE =================

            .addCase(deleteSite.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteSite.fulfilled, (state, action) => {
                state.loading = false;

                state.siteList = state.siteList.filter(
                    (item) => item._id !== action.payload
                );
            })
            .addCase(deleteSite.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            //   get
            .addCase(getProject.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getProject.fulfilled, (state, action) => {
                state.loading = false;
                state.projectList = action.payload?.data || [];
            })
            .addCase(getProject.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // add
            .addCase(addProject.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(addProject.fulfilled, (state, action) => {
                state.loading = false;
                const newProject =
                    action.payload?.data ||
                    action.payload?.project ||
                    action.payload;

                if (newProject) {
                    state.projectList.unshift(newProject);
                }
            })
            .addCase(addProject.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // update
            .addCase(updateProject.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.updateSuccess = false;
            })
            .addCase(updateProject.fulfilled, (state, action) => {
                state.loading = false;
                state.updateSuccess = true;

                const updated =
                    action.payload?.data || action.payload?.project;

                if (updated?._id) {
                    const index = state.projectList.findIndex(
                        (item) => item._id === updated._id
                    );
                    if (index !== -1) {
                        state.projectList[index] = updated;
                    }
                }
            })
            .addCase(updateProject.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // delete
            .addCase(deleteProject.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteProject.fulfilled, (state, action) => {
                state.loading = false;
                state.projectList = state.projectList.filter(
                    (item) => item._id !== action.payload
                );
            })
            .addCase(deleteProject.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export const { resetUpdateStatus } = projectSlice.actions;
export default projectSlice.reducer;
